import type {
  McpCredentialResponseDto,
  McpMultiIdentityDiscoveryResultDto,
  UpsertMcpCredentialDto,
} from './mcp-servers.schema';
import {
  navigateMcpOAuthPopup,
  openBlankMcpOAuthPopup,
  parseMcpOAuthStateNonce,
  waitForMcpOAuthPopupAuthorization,
  waitForMcpOAuthPopupCallback,
} from './mcp-oauth-popup.helpers';

export type WaitForMcpOAuthPopupAuthorization =
  typeof waitForMcpOAuthPopupAuthorization;

export type CompleteMcpOAuthCallback = (input: {
  code: string;
  state: string;
}) => Promise<void>;

export type McpOAuthMessages = {
  oauthPopupBlocked: string;
  oauthAuthorizationFailed: string;
  oauthAuthorizationTimedOut: string;
  oauthAuthorizationWindowClosed: string;
  oauthAuthorizationIncomplete: string;
};

export type McpOAuthAuthorizeStrategy = {
  openReservedWindow?: () => Window | null;
  authorize?: (input: {
    fetchAuthorizeUrl: () => Promise<string>;
    messages: McpOAuthMessages;
    onError: (input: { title: string; description?: string }) => void;
    completeOAuthCallback?: CompleteMcpOAuthCallback;
    reservedPopup?: Window | null;
  }) => Promise<boolean>;
};

export type FinalizeMcpConnectionResult = {
  oauthRequired: boolean;
  oauthAuthorized: boolean;
  connectionTestAttempted: boolean;
  connectionTestSucceeded: boolean;
};

export function isOAuthMcpCredential(
  credential: UpsertMcpCredentialDto | null | undefined,
): credential is Extract<
  UpsertMcpCredentialDto,
  { type: 'OAUTH' | 'OAUTH_DCR' }
> {
  return credential?.type === 'OAUTH' || credential?.type === 'OAUTH_DCR';
}

export async function authorizeMcpOAuthFlow({
  fetchAuthorizeUrl,
  messages,
  onError,
  completeOAuthCallback,
  waitForAuthorization = waitForMcpOAuthPopupAuthorization,
  waitForCallback = waitForMcpOAuthPopupCallback,
  reservedPopup,
}: {
  fetchAuthorizeUrl: () => Promise<string>;
  messages: McpOAuthMessages;
  onError: (input: { title: string; description?: string }) => void;
  completeOAuthCallback?: CompleteMcpOAuthCallback;
  waitForAuthorization?: WaitForMcpOAuthPopupAuthorization;
  waitForCallback?: typeof waitForMcpOAuthPopupCallback;
  reservedPopup?: Window | null;
}): Promise<boolean> {
  let popup: Window | null = null;

  try {
    try {
      popup =
        reservedPopup && !reservedPopup.closed
          ? reservedPopup
          : openBlankMcpOAuthPopup();
    } catch {
      throw new Error(messages.oauthPopupBlocked);
    }

    const authorizeUrl = await fetchAuthorizeUrl();
    const expectedStateNonce = parseMcpOAuthStateNonce(
      new URL(authorizeUrl).searchParams.get('state'),
    );

    navigateMcpOAuthPopup({ popup, authorizeUrl });
    if (completeOAuthCallback) {
      const callbackPayload = await waitForCallback({
        popup,
        authorizationFailedMessage: messages.oauthAuthorizationFailed,
        authorizationTimedOutMessage: messages.oauthAuthorizationTimedOut,
        authorizationWindowClosedMessage:
          messages.oauthAuthorizationWindowClosed,
        expectedStateNonce,
      });

      if (callbackPayload.exchangedViaGateway) {
        return true;
      }

      if (!callbackPayload.code || !callbackPayload.state) {
        throw new Error(messages.oauthAuthorizationIncomplete);
      }

      await completeOAuthCallback({
        code: callbackPayload.code,
        state: callbackPayload.state,
      });

      return true;
    }

    await waitForAuthorization({
      popup,
      authorizationFailedMessage: messages.oauthAuthorizationFailed,
      authorizationTimedOutMessage: messages.oauthAuthorizationTimedOut,
      authorizationWindowClosedMessage: messages.oauthAuthorizationWindowClosed,
    });

    return true;
  } catch (error: unknown) {
    if (popup && !popup.closed) {
      popup.close();
    }

    onError({
      title: messages.oauthAuthorizationFailed,
      description: error instanceof Error ? error.message : undefined,
    });

    return false;
  }
}

export async function authorizeMcpServerOAuth({
  mcpServerId,
  fetchAuthorizeUrl,
  messages,
  onError,
  completeOAuthCallback,
  waitForAuthorization,
  waitForCallback,
  reservedPopup,
}: {
  mcpServerId: string;
  fetchAuthorizeUrl: (serverId: string) => Promise<string>;
  messages: McpOAuthMessages;
  onError: (input: { title: string; description?: string }) => void;
  completeOAuthCallback?: CompleteMcpOAuthCallback;
  waitForAuthorization?: WaitForMcpOAuthPopupAuthorization;
  waitForCallback?: typeof waitForMcpOAuthPopupCallback;
  reservedPopup?: Window | null;
}): Promise<boolean> {
  return authorizeMcpOAuthFlow({
    fetchAuthorizeUrl: () => fetchAuthorizeUrl(mcpServerId),
    messages,
    onError,
    completeOAuthCallback,
    waitForAuthorization,
    waitForCallback,
    reservedPopup,
  });
}

export async function authorizeMcpServerIfNeeded({
  mcpCredential,
  mcpServerId,
  fetchAuthorizeUrl,
  messages,
  onOAuthError,
  completeOAuthCallback,
  waitForAuthorization,
  reservedPopup,
}: {
  mcpCredential: UpsertMcpCredentialDto | null | undefined;
  mcpServerId: string | null | undefined;
  fetchAuthorizeUrl: (serverId: string) => Promise<string>;
  messages: McpOAuthMessages;
  onOAuthError: (input: { title: string; description?: string }) => void;
  completeOAuthCallback?: CompleteMcpOAuthCallback;
  waitForAuthorization?: WaitForMcpOAuthPopupAuthorization;
  reservedPopup?: Window | null;
}): Promise<boolean> {
  if (!mcpCredential || !mcpServerId || !isOAuthMcpCredential(mcpCredential)) {
    return true;
  }

  return authorizeMcpServerOAuth({
    mcpServerId,
    fetchAuthorizeUrl,
    messages,
    onError: onOAuthError,
    completeOAuthCallback,
    waitForAuthorization,
    reservedPopup,
  });
}

export type McpIdentityConnectionStatusMap = Record<
  string,
  {
    connectionSuccess: boolean;
    error: string | null;
  }
>;

const DEFAULT_MCP_IDENTITY_ID = 'default';

export function coerceMcpMultiIdentityDiscoveryResult(
  result: McpMultiIdentityDiscoveryResultDto,
): McpMultiIdentityDiscoveryResultDto {
  const identities =
    result.identities?.length > 0
      ? result.identities
      : [
          {
            id: DEFAULT_MCP_IDENTITY_ID,
            label: 'Default',
            connectionSuccess: result.success,
            error: result.error ?? null,
          },
        ];

  const fallbackIdentityIds = result.success
    ? identities
        .filter((identity) => identity.connectionSuccess)
        .map((identity) => identity.id)
    : [];

  const withIdentityAccess = <T extends { accessibleIdentityIds?: string[] }>(
    item: T,
  ): T => ({
    ...item,
    accessibleIdentityIds: item.accessibleIdentityIds ?? fallbackIdentityIds,
  });

  return {
    ...result,
    identities,
    supportedMethods: result.supportedMethods ?? [],
    probeSummary: result.probeSummary ?? [],
    capabilityMismatches: result.capabilityMismatches ?? [],
    tools: (result.tools ?? []).map(withIdentityAccess),
    resources: (result.resources ?? []).map(withIdentityAccess),
    resourceTemplates: result.resourceTemplates ?? [],
    prompts: (result.prompts ?? []).map(withIdentityAccess),
    resourceCount: result.resourceCount ?? (result.resources ?? []).length,
    promptCount: result.promptCount ?? (result.prompts ?? []).length,
  };
}

export function toMcpIdentityConnectionStatusMap(
  result: McpMultiIdentityDiscoveryResultDto,
): McpIdentityConnectionStatusMap {
  return Object.fromEntries(
    coerceMcpMultiIdentityDiscoveryResult(result).identities.map((identity) => [
      identity.id,
      {
        connectionSuccess: identity.connectionSuccess,
        error: identity.error,
      },
    ]),
  );
}

export function formatMcpIdentityConnectionFailureDescription(
  identities: McpMultiIdentityDiscoveryResultDto['identities'] | undefined,
): string | undefined {
  const failedIdentities = (identities ?? []).filter(
    (identity) => !identity.connectionSuccess,
  );

  if (failedIdentities.length === 0) {
    return undefined;
  }

  return failedIdentities
    .map((identity) =>
      identity.error ? `${identity.label}: ${identity.error}` : identity.label,
    )
    .join('\n');
}

export async function testMcpConnectionWithFeedback({
  testConnection,
  onSuccess,
  onError,
  onResult,
}: {
  testConnection: () => Promise<McpMultiIdentityDiscoveryResultDto>;
  onSuccess: (result: McpMultiIdentityDiscoveryResultDto) => void;
  onError: (input: {
    result: McpMultiIdentityDiscoveryResultDto | null;
    description?: string;
  }) => void;
  onResult?: (result: McpMultiIdentityDiscoveryResultDto) => void;
}): Promise<boolean> {
  try {
    const result = coerceMcpMultiIdentityDiscoveryResult(
      await testConnection(),
    );

    onResult?.(result);
    const allIdentitiesConnected =
      result.identities.length === 0 ||
      result.identities.every((identity) => identity.connectionSuccess);

    if (result.success && allIdentitiesConnected) {
      onSuccess(result);

      return true;
    }

    if (result.success) {
      onSuccess(result);

      return false;
    }

    onError({
      result,
      description: formatMcpIdentityConnectionFailureDescription(
        result.identities,
      ),
    });

    return false;
  } catch (error: unknown) {
    onError({
      result: null,
      description: error instanceof Error ? error.message : undefined,
    });

    return false;
  }
}

export async function finalizeMcpConnectionAfterSave({
  mcpCredential,
  mcpServerId,
  upsertCredential,
  fetchAuthorizeUrl,
  testConnection,
  messages,
  onOAuthError,
  onTestResult,
  onTestSuccess,
  onTestError,
  completeOAuthCallback,
  waitForAuthorization,
  reservedOAuthPopup,
  oauthStrategy,
}: {
  mcpCredential: UpsertMcpCredentialDto | null | undefined;
  mcpServerId: string | null | undefined;
  upsertCredential?: (input: {
    serverId: string;
    credential: UpsertMcpCredentialDto;
  }) => Promise<void | McpCredentialResponseDto>;
  fetchAuthorizeUrl: (serverId: string) => Promise<string>;
  testConnection: () => Promise<McpMultiIdentityDiscoveryResultDto>;
  messages: McpOAuthMessages;
  onOAuthError: (input: { title: string; description?: string }) => void;
  onTestResult?: (result: McpMultiIdentityDiscoveryResultDto) => void;
  onTestSuccess: (result: McpMultiIdentityDiscoveryResultDto) => void;
  onTestError: (input: {
    result: McpMultiIdentityDiscoveryResultDto | null;
    description?: string;
  }) => void;
  completeOAuthCallback?: CompleteMcpOAuthCallback;
  waitForAuthorization?: WaitForMcpOAuthPopupAuthorization;
  reservedOAuthPopup?: Window | null;
  oauthStrategy?: McpOAuthAuthorizeStrategy;
}): Promise<FinalizeMcpConnectionResult> {
  const oauthRequired = Boolean(
    mcpCredential && mcpServerId && isOAuthMcpCredential(mcpCredential),
  );

  let reservedPopup =
    reservedOAuthPopup && !reservedOAuthPopup.closed
      ? reservedOAuthPopup
      : null;

  if (oauthRequired && !reservedPopup) {
    if (oauthStrategy?.openReservedWindow) {
      reservedPopup = oauthStrategy.openReservedWindow();
    } else if (!oauthStrategy?.authorize) {
      try {
        reservedPopup = openBlankMcpOAuthPopup();
      } catch {
        reservedPopup = null;
      }
    }
  }

  if (mcpCredential && mcpServerId && upsertCredential) {
    await upsertCredential({
      serverId: mcpServerId,
      credential: mcpCredential,
    });
  }

  const oauthAuthorized =
    !oauthRequired || !mcpServerId
      ? true
      : oauthStrategy?.authorize
        ? await oauthStrategy.authorize({
            fetchAuthorizeUrl: () => fetchAuthorizeUrl(mcpServerId),
            messages,
            onError: onOAuthError,
            completeOAuthCallback,
            reservedPopup,
          })
        : await authorizeMcpServerIfNeeded({
            mcpCredential,
            mcpServerId,
            fetchAuthorizeUrl,
            messages,
            onOAuthError,
            completeOAuthCallback,
            waitForAuthorization,
            reservedPopup,
          });

  if (!oauthAuthorized) {
    return {
      oauthRequired,
      oauthAuthorized: false,
      connectionTestAttempted: false,
      connectionTestSucceeded: false,
    };
  }

  const connectionTestSucceeded = await testMcpConnectionWithFeedback({
    testConnection,
    onResult: (result) => {
      onTestResult?.(result);
    },
    onSuccess: onTestSuccess,
    onError: onTestError,
  });

  return {
    oauthRequired,
    oauthAuthorized: true,
    connectionTestAttempted: true,
    connectionTestSucceeded,
  };
}
